@if (handoffResponse) {
  <app-handoff-interstitial [redirectUrl]="adminSigninUrl"></app-handoff-interstitial>
} @else {
  <div class="layout">
    <aside class="pitch">
      <h1 class="pitch-headline">{{ 'signup.pitch.headline' | transloco }}</h1>
      <p class="pitch-copy">{{ 'signup.pitch.copy' | transloco }}</p>
    </aside>

    <form
      class="signup-form"
      [formGroup]="form"
      (ngSubmit)="submit()"
      novalidate
    >
      <app-signup-form-cluster
        [title]="'signup.clusters.account.title' | transloco"
        [helperText]="'signup.clusters.account.helper' | transloco"
      >
        <mat-form-field appearance="fill">
          <mat-label>{{ 'signup.fields.email' | transloco }}</mat-label>
          <input
            matInput
            type="email"
            autocomplete="email"
            formControlName="operatorEmail"
            maxlength="254"
            required
          />
          @if (operatorEmail.errors?.['required'] && operatorEmail.touched) {
            <mat-error>{{ 'signup.errors.emailRequired' | transloco }}</mat-error>
          }
          @if (operatorEmail.errors?.['email'] && operatorEmail.touched) {
            <mat-error>{{ 'signup.errors.emailInvalid' | transloco }}</mat-error>
          }
          @if (operatorEmail.errors?.['backend']) {
            <mat-error>{{ operatorEmail.errors?.['backend'] }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="fill">
          <mat-label>{{ 'signup.fields.password' | transloco }}</mat-label>
          <input
            matInput
            [type]="passwordVisible ? 'text' : 'password'"
            autocomplete="new-password"
            formControlName="operatorPassword"
            required
          />
          <button
            type="button"
            mat-icon-button
            matSuffix
            (click)="togglePassword()"
            [attr.aria-label]="(passwordVisible ? 'signup.passwordToggle.hide' : 'signup.passwordToggle.show') | transloco"
            [attr.aria-pressed]="passwordVisible"
          >
            <mat-icon>{{ passwordVisible ? 'visibility_off' : 'visibility' }}</mat-icon>
          </button>
          @if (operatorPassword.errors?.['required'] && operatorPassword.touched) {
            <mat-error>{{ 'signup.errors.passwordRequired' | transloco }}</mat-error>
          }
          @if (operatorPassword.errors?.['minlength'] && operatorPassword.touched) {
            <mat-error>{{ 'signup.errors.passwordMinLength' | transloco }}</mat-error>
          }
          @if (operatorPassword.errors?.['backend']) {
            <mat-error>{{ operatorPassword.errors?.['backend'] }}</mat-error>
          }
        </mat-form-field>
      </app-signup-form-cluster>

      <app-signup-form-cluster
        [title]="'signup.clusters.tenant.title' | transloco"
        [helperText]="'signup.clusters.tenant.helper' | transloco"
      >
        <mat-form-field appearance="fill">
          <mat-label>{{ 'signup.fields.companyName' | transloco }}</mat-label>
          <input
            matInput
            type="text"
            autocomplete="organization"
            formControlName="companyName"
            maxlength="128"
            required
          />
          @if (companyName.errors?.['required'] && companyName.touched) {
            <mat-error>{{ 'signup.errors.companyNameRequired' | transloco }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="fill">
          <mat-label>{{ 'signup.fields.displayName' | transloco }}</mat-label>
          <input
            matInput
            type="text"
            formControlName="tenantDisplayName"
            maxlength="128"
            required
          />
          @if (tenantDisplayName.errors?.['required'] && tenantDisplayName.touched) {
            <mat-error>{{ 'signup.errors.displayNameRequired' | transloco }}</mat-error>
          }
        </mat-form-field>
      </app-signup-form-cluster>

      <!-- Platform Terms & Permissions — loaded from GET /terms/active (Story 38.4) -->
      @if (termsLoading) {
        <div class="terms-loading">{{ 'signup.terms.loading' | transloco }}</div>
      }
      @if (termsLoadError) {
        <p class="terms-error" role="alert">{{ 'signup.terms.loadError' | transloco }}</p>
      }
      @if (!termsLoading && !termsLoadError && activeTerms.length > 0) {
        <app-signup-form-cluster
          [title]="'signup.terms.clusterTitle' | transloco"
          [helperText]="'signup.terms.clusterHelper' | transloco"
        >
          @for (term of activeTerms; track term.id) {
            <div class="term-row" [class.term-required]="term.kind === 'term'">
              <mat-checkbox
                [checked]="isTermAccepted(term.id)"
                (change)="toggleTerm(term.id)"
                [required]="term.kind === 'term'"
              >
                {{ term.resume }}
                @if (term.kind === 'permission') {
                  <span class="term-optional-badge">{{ 'signup.terms.optional' | transloco }}</span>
                }
              </mat-checkbox>
              <button
                type="button"
                mat-button
                class="term-full-text-btn"
                (click)="openFullText(term)"
              >
                {{ 'signup.terms.readFull' | transloco }}
              </button>
              @if (term.kind === 'term' && termsMissingAcceptance && !isTermAccepted(term.id)) {
                <p class="term-error" role="alert">{{ 'signup.terms.termRequired' | transloco }}</p>
              }
            </div>
          }
        </app-signup-form-cluster>
      }

      <app-signup-form-cluster
        [title]="'signup.clusters.contact.title' | transloco"
        [helperText]="'signup.clusters.contact.helper' | transloco"
      >
        <mat-form-field appearance="fill">
          <mat-label>{{ 'signup.fields.contactName' | transloco }}</mat-label>
          <input matInput type="text" formControlName="contactName" maxlength="128" required />
          @if (contactName.errors?.['required'] && contactName.touched) {
            <mat-error>{{ 'signup.errors.contactNameRequired' | transloco }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="fill">
          <mat-label>{{ 'signup.fields.contactEmail' | transloco }}</mat-label>
          <input
            matInput
            type="email"
            autocomplete="email"
            formControlName="contactEmail"
            maxlength="254"
            required
          />
          @if (contactEmail.errors?.['required'] && contactEmail.touched) {
            <mat-error>{{ 'signup.errors.contactEmailRequired' | transloco }}</mat-error>
          }
          @if (contactEmail.errors?.['email'] && contactEmail.touched) {
            <mat-error>{{ 'signup.errors.emailInvalid' | transloco }}</mat-error>
          }
          @if (contactEmail.errors?.['backend']) {
            <mat-error>{{ contactEmail.errors?.['backend'] }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="fill">
          <mat-label>{{ 'signup.fields.contactPhone' | transloco }}</mat-label>
          <input matInput type="tel" autocomplete="tel" formControlName="contactPhone" maxlength="128" />
        </mat-form-field>

        <mat-form-field appearance="fill">
          <mat-label>{{ 'signup.fields.street' | transloco }}</mat-label>
          <input matInput type="text" autocomplete="street-address" formControlName="addressStreet" maxlength="128" required />
          @if (addressStreet.errors?.['required'] && addressStreet.touched) {
            <mat-error>{{ 'signup.errors.streetRequired' | transloco }}</mat-error>
          }
        </mat-form-field>

        <div class="address-row">
          <mat-form-field appearance="fill" class="address-city">
            <mat-label>{{ 'signup.fields.city' | transloco }}</mat-label>
            <input matInput type="text" autocomplete="address-level2" formControlName="addressCity" maxlength="128" required />
            @if (addressCity.errors?.['required'] && addressCity.touched) {
              <mat-error>{{ 'signup.errors.cityRequired' | transloco }}</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="fill" class="address-postal">
            <mat-label>{{ 'signup.fields.postalCode' | transloco }}</mat-label>
            <input matInput type="text" autocomplete="postal-code" formControlName="addressPostalCode" maxlength="128" required />
            @if (addressPostalCode.errors?.['required'] && addressPostalCode.touched) {
              <mat-error>{{ 'signup.errors.postalCodeRequired' | transloco }}</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="fill" class="address-country">
            <mat-label>{{ 'signup.fields.country' | transloco }}</mat-label>
            <input matInput type="text" autocomplete="country" formControlName="addressCountry" maxlength="2" required />
            @if (addressCountry.errors?.['required'] && addressCountry.touched) {
              <mat-error>{{ 'signup.errors.countryRequired' | transloco }}</mat-error>
            }
            @if (addressCountry.errors?.['pattern'] && addressCountry.touched) {
              <mat-error>{{ 'signup.errors.countryPattern' | transloco }}</mat-error>
            }
          </mat-form-field>
        </div>

        <mat-form-field appearance="fill">
          <mat-label>{{ 'signup.fields.vatId' | transloco }}</mat-label>
          <input matInput type="text" autocomplete="off" formControlName="vatId" maxlength="128" />
          <mat-hint>{{ 'signup.hints.vatId' | transloco }}</mat-hint>
          @if (vatId.errors?.['vatId'] && vatId.touched) {
            <mat-error>{{ 'signup.errors.vatIdInvalid' | transloco }}</mat-error>
          }
          @if (vatId.errors?.['backend']) {
            <mat-error>{{ vatId.errors?.['backend'] }}</mat-error>
          }
        </mat-form-field>
      </app-signup-form-cluster>

      <div class="submit-row">
        <button
          #submitButton
          type="submit"
          mat-flat-button
          color="primary"
          class="submit-button"
          [disabled]="submitting || termsLoading || termsLoadError"
          [attr.aria-busy]="captchaSolving ? 'true' : null"
        >
          {{ (submitting ? 'signup.submit.submitting' : 'signup.submit.idle') | transloco }}
        </button>
        <p class="visually-hidden" role="status" aria-live="polite">{{ captchaSolving ? ('signup.submit.verifying' | transloco) : '' }}</p>
      </div>

      <ul class="trust-bar" [attr.aria-label]="'common.trustAriaLabel' | transloco">
        @for (chip of trustChips; track chip) {
          <li class="trust-chip">{{ chip | transloco }}</li>
        }
      </ul>
    </form>
  </div>
}
